// --- Count non-manifold edges in a model (Orca/PrusaSlicer-style topology check)
//
// Mirrors how libslic3r builds mesh topology: coincident vertices are welded by
// position first, then every undirected edge is mapped to the number of facets
// that reference it. An edge shared by exactly 2 facets is manifold; an edge
// shared by >2 facets is non-manifold; an edge with only 1 facet is an open
// (boundary) edge. Raw STL/buffer geometry duplicates vertices, so welding by
// position is required for the counts to be meaningful.
//
// @param {THREE.BufferGeometry} geom
// @param {object} [opts]
// @param {number} [opts.weldTolerance=1e-6] - quantization step for welding verts
// @returns {{ nonManifoldEdges:number, openEdges:number, totalEdges:number, elapsedMs:number }}
export function countNonManifoldEdges(geom, opts = {}) {
  const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  const { weldTolerance = 1e-6 } = opts;

  const pos = geom.attributes.position;
  const idx = geom.index ? geom.index.array : null;
  const vcount = pos.count;

  // --- Weld coincident vertices by quantized position -> canonical id
  const invTol = 1 / weldTolerance;
  const weldMap = new Map();   // "qx_qy_qz" -> canonical id
  const canonical = new Int32Array(vcount);
  let nextId = 0;
  for (let i = 0; i < vcount; i++) {
    const qx = Math.round(pos.getX(i) * invTol);
    const qy = Math.round(pos.getY(i) * invTol);
    const qz = Math.round(pos.getZ(i) * invTol);
    const key = `${qx}_${qy}_${qz}`;
    let id = weldMap.get(key);
    if (id === undefined) { id = nextId++; weldMap.set(key, id); }
    canonical[i] = id;
  }

  // --- Count how many facets reference each undirected edge
  const edgeCount = new Map(); // "minId_maxId" -> facet count
  const addEdge = (a, b) => {
    const ca = canonical[a], cb = canonical[b];
    if (ca === cb) return; // degenerate edge, ignore
    const key = ca < cb ? `${ca}_${cb}` : `${cb}_${ca}`;
    edgeCount.set(key, (edgeCount.get(key) || 0) + 1);
  };

  const tris = idx ? idx.length : Math.floor(vcount / 3) * 3;
  for (let i = 0; i < tris; i += 3) {
    const a = idx ? idx[i]     : i;
    const b = idx ? idx[i + 1] : i + 1;
    const c = idx ? idx[i + 2] : i + 2;
    addEdge(a, b);
    addEdge(b, c);
    addEdge(c, a);
  }

  let nonManifoldEdges = 0;
  let openEdges = 0;
  for (const count of edgeCount.values()) {
    if (count > 2) nonManifoldEdges++;
    else if (count === 1) openEdges++;
  }

  const elapsedMs = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0;

  console.log(
    `[countNonManifoldEdges] non-manifold edges: ${nonManifoldEdges}, ` +
    `open (boundary) edges: ${openEdges}, total unique edges: ${edgeCount.size}, ` +
    `time taken: ${elapsedMs.toFixed(2)} ms`
  );

  return { nonManifoldEdges, openEdges, totalEdges: edgeCount.size, elapsedMs };
}
